<template>
  <v-menu
    ref="menu"
    v-model="show"
    data-cy="date-field-menu"
    :close-on-content-click="false"
    bottom
    max-width="450"
  >
    <template #activator="{ props }">
      <v-text-field
        v-bind="props"
        v-model="date"
        data-cy="date-field-text-field"
        append-inner-icon="mdi-calendar-blank-outline"
        :hint="hint"
        :label="label"
        :placeholder="placeholder"
        :required="required"
        :rules="rules"
        :clearable="clearable"
        type="date"
        readonly
        @click:append-inner.stop="openDatePicker"
        @click:clear.stop="clearDate"
        @click.stop
      />
    </template>
    <v-date-picker
      ref="picker"
      v-model="date"
      data-cy="date-field-date-picker"
      :min="minDate"
      :max="maxDate"
      color="primary"
      :locale="userLocale"
      :title="t('components.datePicker.title')"
      :header="t('components.datePicker.header')"
      @update:model-value="show = false"
    />
  </v-menu>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useDisplay } from 'vuetify';
import dayjs from 'dayjs';
import I18n from '@/plugins/i18n';
import { useLabel } from '@/composables/useLabel';

export default defineComponent({
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    birthdayPicker: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: String,
      default: null,
    },
    maxDate: {
      type: String,
      default: null,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array as () => ((value) => string | boolean)[],
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { t } = useLabel();
    const show = ref(false);
    const date = computed({
      get: () => props.modelValue || '',
      set: (val: string | Date) => {
        if (val instanceof Date) {
          emit('update:modelValue', dayjs(val).format('YYYY-MM-DD'));
        } else {
          emit('update:modelValue', val);
        }
      },
    });
    const display = useDisplay();

    const userLocale = computed(() => {
      return I18n.getUserLanguage();
    });

    watch(show, (val) => {
      if (props.birthdayPicker && val && !date.value) {
        setTimeout(() => {
          // Handle picker ref
          date.value = dayjs().subtract(55, 'year').format('YYYY-MM-DD');
        });
      }
    });

    const save = () => {
      // Handle menu save
      show.value = false;
    };

    // Ensure clicking the icon focuses or opens the picker on small devices as well
    const openDatePicker = async () => {
      show.value = true;
      await Promise.resolve();
      try {
        const picker = document.querySelector('[data-cy="date-field-date-picker"]');
        if (picker) {
          const focusable = picker.querySelector('input, button, [tabindex]');
          if (focusable) (focusable as HTMLElement).focus();
        }
      } catch {
        // Focus target may not exist yet
      }
    };

    const clearDate = () => {
      date.value = '';
      show.value = false;
    };

    return {
      t,
      show,
      date,
      userLocale,
      save,
      display,
      openDatePicker,
      clearDate,
    };
  },
});
</script>


<!-- Hacky fix to update placeholder for date picker -->
<style>
input[type='date']::-webkit-datetime-edit,
input[type='date']::-webkit-datetime-edit-fields-wrapper,
input[type='date']::-webkit-datetime-edit-year-field,
input[type='date']::-webkit-datetime-edit-month-field,
input[type='date']::-webkit-datetime-edit-day-field,
input[type='date']::-webkit-datetime-edit-text {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}
</style>
